import React, { useEffect, useRef, memo } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { useScreenInit } from '../useScreenInit';
export function Events() {
  const {
    section
  } = useScreenInit();
  const heroRef = useRef<HTMLDivElement>(null);
  const weddingsRef = useRef<HTMLDivElement>(null);
  const corporateRef = useRef<HTMLDivElement>(null);
  const milestonesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {
      hero: heroRef,
      weddings: weddingsRef,
      corporate: corporateRef,
      milestones: milestonesRef
    };
    if (section && refs[section]?.current) {
      refs[section].current?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [section]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="min-h-screen bg-#[#050505] selection:bg-#[#992f74] selection:text-white font-montserrat" data-id="element-344">
      <Header data-id="element-345" />

      <main data-id="element-346">
        {/* Immersive Hero Section */}
        <section ref={heroRef} className="relative w-full h-[60vh] flex items-center justify-center overflow-hidden border-b border-white/10" data-id="element-347">
          <div className="absolute inset-0 z-0" data-id="element-348">
            <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&q=80&w=2000" alt="Elegant Event" className="w-full h-full object-cover opacity-40 scale-105 animate-slow-zoom" data-id="element-349" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#050505]" data-id="element-350"></div>
          </div>

          <div className="relative z-10 text-center px-6 max-w-5xl mx-auto mt-20" data-id="element-351">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} data-id="element-352">
              <h1 className="text-5xl md:text-7xl font-montserrat font-black italic text-white mb-6 drop-shadow-2xl" data-id="element-353">
                Some of our <br data-id="element-354" />
                <span className="text-[#992f74]" data-id="element-355">Events</span>
              </h1>
              <div className="w-24 h-[1px] bg-[#992f74] mx-auto mb-8" data-id="element-356"></div>
              <p className="text-white/70 text-base md:text-xl leading-relaxed max-w-2xl mx-auto font-light" data-id="element-357">
                Dynamic, memorable experiences that keep people talking long
                after the event ends.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content Section */}
        <section className="py-24 px-6 relative" data-id="element-358">
          <div className="max-w-7xl mx-auto" data-id="element-359">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-360">
              {/* Text Content */}
              <motion.div initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="space-y-8" data-id="element-361">
                <h2 className="text-3xl md:text-5xl font-montserrat font-bold italic text-white mb-4 leading-tight" data-id="element-362">
                  Engaging from <br data-id="element-363" />
                  <span className="text-[#992f74]" data-id="element-364">Start to Finish</span>
                </h2>

                <div className="space-y-6 text-white/80 font-light leading-relaxed text-lg" data-id="element-365">
                  <p data-id="element-366">
                    At Sips & Nibblez, we create events that keep guests engaged
                    from start to finish. Our live mixology sessions, tasting
                    and pairing experiences, and interactive cocktail bar
                    moments bring energy and excitement to any gathering.
                  </p>
                  <p data-id="element-367">
                    Guests stay entertained with beautifully crafted drinks made
                    on the spot, flavorful bites served throughout the event,
                    and immersive themed nights that transform the entire
                    atmosphere.
                  </p>
                  <p data-id="element-368">
                    Whether it’s a birthday, corporate mixer, or private
                    celebration, we deliver a dynamic, memorable experience that
                    keeps people talking long after the event ends.
                  </p>
                </div>
              </motion.div>

              {/* Image Collage */}
              <div className="relative h-[500px] md:h-[600px]" data-id="element-369">
                <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8
              }} className="absolute top-0 right-0 w-4/5 h-[80%] rounded-2xl overflow-hidden shadow-2xl z-10" data-id="element-370">
                  <img src="https://cdn.magicpatterns.com/uploads/5yN64wqTPGjWVnhgLzAFWw/Image_(55).jpg" alt="Event Atmosphere" className="w-full h-full object-cover" data-id="element-371" />
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 50
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                delay: 0.2
              }} className="absolute bottom-0 left-0 w-3/5 h-[60%] rounded-2xl overflow-hidden shadow-2xl z-20 border-4 border-[#050505]" data-id="element-372">
                  <img src="https://cdn.magicpatterns.com/uploads/wVm7kHz2wF77pb8aL32vqL/Image_(46).jpg" alt="Cocktail Details" className="w-full h-full object-cover" data-id="element-373" />
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Adaptable Spaces Section */}
        <section className="py-24 px-6 relative bg-[#0A0A0A] border-y border-white/5" data-id="element-374">
          <div className="max-w-7xl mx-auto" data-id="element-375">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-376">
              {/* Image */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="order-2 lg:order-1 relative h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-2xl" data-id="element-377">
                <img src="https://sipsandnibblez.com/wp-content/uploads/2025/11/image-64.png" alt="Outdoor Event Setup" className="w-full h-full object-cover" data-id="element-378" />
              </motion.div>

              {/* Text Content */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="order-1 lg:order-2 space-y-8" data-id="element-379">
                <h2 className="text-3xl md:text-5xl font-montserrat font-bold italic text-white mb-4 leading-tight" data-id="element-380">
                  Adaptable to <br data-id="element-381" />
                  <span className="text-[#992f74]" data-id="element-382">Any Space</span>
                </h2>

                <div className="space-y-6 text-white/80 font-light leading-relaxed text-lg" data-id="element-383">
                  <p data-id="element-384">
                    Whether it’s an outdoor setup, backyard party, rooftop, or
                    rented venue, we adapt our setup to fit the space safely and
                    efficiently.
                  </p>
                  <p data-id="element-385">
                    Our team ensures that no matter the location, the quality of
                    service, presentation, and guest experience remains
                    flawlessly high-end.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Premium Packages Section */}
        <section className="py-24 px-6 relative" data-id="element-386">
          <div className="max-w-7xl mx-auto text-center space-y-16" data-id="element-387">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-388">
              <h2 className="text-3xl md:text-5xl font-montserrat font-bold italic text-white mb-6" data-id="element-389">
                Premium On-Site <span className="text-[#992f74]" data-id="element-390">Service</span>
              </h2>
              <div className="w-16 h-[2px] bg-[#992f74] mx-auto mb-8" data-id="element-391"></div>
              <p className="text-white/80 font-light text-lg max-w-3xl mx-auto leading-relaxed" data-id="element-392">
                Yes, we offer premium on-site service, including live mixology,
                gourmet bite catering, and full event support.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left" data-id="element-393">
              <div className="bg-[#111] p-8 rounded-2xl border border-white/5 hover:border-[#992f74]/30 transition-colors" data-id="element-394">
                <h3 className="text-xl font-montserrat text-white mb-4 italic" data-id="element-395">
                  Live Mixology
                </h3>
                <p className="text-white/60 font-light text-sm leading-relaxed" data-id="element-396">
                  On-site mixologists crafting beautiful drinks and providing
                  elevated guest interaction.
                </p>
              </div>
              <div className="bg-[#111] p-8 rounded-2xl border border-white/5 hover:border-[#992f74]/30 transition-colors" data-id="element-397">
                <h3 className="text-xl font-montserrat text-white mb-4 italic" data-id="element-398">
                  Gourmet Bites
                </h3>
                <p className="text-white/60 font-light text-sm leading-relaxed" data-id="element-399">
                  Dedicated gourmet bites service with stylish presentation to
                  complement your drinks.
                </p>
              </div>
              <div className="bg-[#111] p-8 rounded-2xl border border-white/5 hover:border-[#992f74]/30 transition-colors" data-id="element-400">
                <h3 className="text-xl font-montserrat text-white mb-4 italic" data-id="element-401">
                  Full Setup
                </h3>
                <p className="text-white/60 font-light text-sm leading-relaxed" data-id="element-402">
                  Complete event support, ensuring a seamless, stress-free
                  experience from start to finish.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 bg-[#992f74] text-white text-center" data-id="element-403">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto space-y-8" data-id="element-404">
            <h2 className="text-4xl md:text-6xl font-montserrat font-black italic tracking-wide" data-id="element-405">
              Ready to Celebrate?
            </h2>
            <p className="text-white/90 text-lg font-light" data-id="element-406">
              Let us bring the energy, excitement, and flavor to your next
              gathering.
            </p>
            <a href="/book" className="inline-block bg-white text-[#992f74] px-12 py-4 text-sm font-bold tracking-widest uppercase hover:bg-black hover:text-white transition-colors duration-300 shadow-2xl mt-8" data-id="element-407">
              Book Your Event
            </a>
          </motion.div>
        </section>
      </main>
      <Footer data-id="element-408" />
    </motion.div>;
}